import { FC, useCallback, useMemo } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/store';

import ControlBarButton from '@3.0/Buttons/ControlBarButtons/ControlBarButton';
import { isRDBMSDataType } from 'Modules/Studio/DataSources/components/Datasets/IngestSettings/components/IncrementalIngestionSettings/utilities/IncrementalIngestionSettings.helpers';
import { IDataSourceItem } from 'Modules/Studio/DataSources/redux/initialState';
import { goToTableView } from '../redux/IngestionDataSourceList.slice';
import { IActiveIngestion } from 'Modules/Studio/components/StudioInfo/redux/ingestion.interface';
import { INGESTION_CONSTANTS } from '../../../utilities/Ingestion.constants';
import { useIngestionByDataSourceContext } from '../../context/DataSourceIngestion.context';
import { getTooltipTextForTables } from '../../../utilities';

type TProps = {
  dataSource: IDataSourceItem;
  activeIngestion: IActiveIngestion | null;
};

const IngestionByTableButton: FC<TProps> = ({ dataSource, activeIngestion }) => {
  const { id, name, dataSourceType } = dataSource;

  const { isLoadingFullIngestionStatus, isFullIngestionActive, isStopping } =
    useIngestionByDataSourceContext();

  const view = useAppSelector((state) => state.ingestionDataSourceList.view);

  const isDisabled = useMemo(() => {
    return (
      isLoadingFullIngestionStatus ||
      isFullIngestionActive ||
      isStopping ||
      activeIngestion?.status === INGESTION_CONSTANTS.TRIGGERED_STOP ||
      // When the ingestion is running from the data source level
      (activeIngestion?.status === INGESTION_CONSTANTS.RUNNING &&
        Object.keys(activeIngestion?.tables).length === 0)
    );
  }, [activeIngestion, isLoadingFullIngestionStatus, isFullIngestionActive, isStopping]);

  // disallow if not RDBMS type
  // @ts-ignore - isRDBMSDataType is not properly types
  const allowIngestionByTable = useMemo(() => isRDBMSDataType(dataSourceType), [dataSourceType]);

  const tooltipText = useMemo(
    () =>
      getTooltipTextForTables(
        allowIngestionByTable,
        isFullIngestionActive,
        isStopping,
        isLoadingFullIngestionStatus,
        activeIngestion
      ),
    [
      allowIngestionByTable,
      isFullIngestionActive,
      isLoadingFullIngestionStatus,
      activeIngestion,
      isStopping,
    ]
  );

  const dispatch = useAppDispatch();

  const handleGoToTableView = useCallback(() => {
    dispatch(goToTableView({ selectedDataSource: { id, name, dataSourceType } }));
  }, [dispatch, id, name, dataSourceType]);

  if (view === 'tables') return null;

  return (
    <ControlBarButton
      data-testid={`${id}-ingest-by-table-button`}
      disabled={!allowIngestionByTable || isDisabled}
      className="tw:px-2"
      onClick={handleGoToTableView}
    >
      <Tooltip enterDelay={500} placement="left" title={tooltipText}>
        <ChevronRight style={{ width: 20 }} />
      </Tooltip>
    </ControlBarButton>
  );
};

export default IngestionByTableButton;
